import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export type ExtraCheckbox = {
  key: string;
  label: string;
  defaultChecked?: boolean;
};

/**
 * Dialog genérico para acciones con motivo opcional o requerido.
 * Reutilizado desde detalle de contrato y detalle de cliente (F14.4).
 *
 * F92: ahora soporta `extraCheckbox` opcional para flags adicionales
 * (ej. "marcar equipo como ya recogido" en cancelación de contrato comodato).
 * Los flags se entregan a `onConfirm` como segundo arg `extras`.
 */
export function ActionDialog({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  actionVariant = "default",
  motivoRequired,
  isPending,
  onConfirm,
  extraCheckbox,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: string;
  actionLabel: string;
  actionVariant?: "default" | "destructive";
  motivoRequired: boolean;
  isPending: boolean;
  onConfirm: (
    motivo: string | null,
    extras?: Record<string, boolean>,
  ) => Promise<string | void>;
  extraCheckbox?: ExtraCheckbox;
}) {
  const [motivo, setMotivo] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [extraChecked, setExtraChecked] = useState<boolean>(
    extraCheckbox?.defaultChecked ?? false,
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setMotivo("");
          setErr(null);
          setExtraChecked(extraCheckbox?.defaultChecked ?? false);
        }
        onOpenChange(v);
      }}
    >
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent>
        <Label>Motivo {motivoRequired && "*"}</Label>
        <Textarea
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        {extraCheckbox && (
          <label className="mt-3 flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              checked={extraChecked}
              onChange={(e) => setExtraChecked(e.target.checked)}
              className="mt-0.5 h-3 w-3"
            />
            <span>{extraCheckbox.label}</span>
          </label>
        )}
        {err && <div className="mt-2 text-xs text-destructive">{err}</div>}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button
          variant={actionVariant}
          size="sm"
          disabled={isPending}
          onClick={async () => {
            setErr(null);
            const extras = extraCheckbox
              ? { [extraCheckbox.key]: extraChecked }
              : undefined;
            const msg = await onConfirm(motivo.trim() || null, extras);
            if (msg) setErr(msg);
          }}
        >
          {isPending ? "Procesando…" : actionLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
